import { Typography } from "@graphy/ui";
import classNames from "classnames";
import Image, { StaticImageData } from "next/image";
import Heading from "../atoms/Heading";
import CtaButtonGroup, { CtaType } from "../molecules/CtaButtonGroup";

export type LevelUpItemPropsType = {
  title: string;
  description: string;
  coverImage: string;
  image: {
    src: StaticImageData;
    alt: string;
  };
  highlights: {
    title: string;
    description: string;
  }[];
  ctas: CtaType[];
  position: number;
};

const LevelUpItem = ({
  title,
  description,
  coverImage,
  image,
  highlights,
  ctas,
  position,
}: LevelUpItemPropsType) => {
  const isEvenPosition = position % 2 === 0;
  return (
    <div className={classNames("rounded-3xl bg-cover bg-left-top bg-no-repeat md:mx-0", isEvenPosition ? "bg-[url('/images/inverted-gradient-bg.png')]" : "bg-[url('/images/gradient-bg.png')]")}>
      <div
        className={classNames(
          "page-max-width mx-0 flex justify-between gap-6 p-4 md:mx-auto md:items-center md:p-10",
          isEvenPosition
            ? "flex-col-reverse  md:flex-row-reverse"
            : "flex-col-reverse md:flex-row"
        )}
      >
        <div>
          <h3 className="text-left text-title-20 font-semibold text-grey-4 sm:text-title-24">
            {title}
          </h3>
          <Typography
            fontSize="text-title6"
            fontWeight="font-normal"
            colorClass="text-grey-4"
            className="mt-[6px]"
          >
            {description}
          </Typography>
          <div className="flex flex-col gap-3 my-12">
            {highlights.map((highlight, index) => {
              return (
                <div key={highlight.title} className="flex items-start gap-2">
                  <div className="h-5 flex items-center">
                  <div className="min-w-5 h-[14px] min-w-[14px] rounded-full bg-purple-2"></div>
                  </div>
                  <div>
                    <p className=" text-title-14 font-semibold text-darkSlate01">
                      {highlight.title}
                    </p>
                    <p className=" text-body-12 font-normal text-grey-4 ">
                      {highlight.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          <CtaButtonGroup ctas={ctas} gap="gap-4" fullWidthOnMobile={false} />
        </div>
        <Image
          src={image?.src!}
          alt={image?.alt!}
          width={0}
          height={0}
          sizes="100vw"
          className="mt-4 h-auto w-full md:mt-0 md:h-[320px] md:w-[446px]"
        />
      </div>
    </div>
  );
};

export type LevelUpSectionPropsType = {
  langFile: string;
  data: {
    type: string;
    items: {
      title: string;
      description: string;
      coverImage: string;
      image: {
        src: StaticImageData;
        alt: string;
      };
      highlights: {
        title: string;
        description: string;
      }[];
      ctas: CtaType[];
    }[];
  };
};

const LevelUpSection = ({ data }: LevelUpSectionPropsType) => {
  const { items } = data;
  return (
    <div className="container flex flex-col items-center justify-center gap-8 sm:gap-6">
      <div className="flex flex-col items-center justify-center gap-4">
        <Heading
          heading={[
            {
              text: "Level up ",
              highlight: true,
            },
            {
              text: "your online monetization game with Graphy",
            },
          ]}
          as="h2"
          fontStyle="text-title-20 sm:text-title-32"
        />
      </div>

      <div className="flex flex-col gap-12 overflow-hidden">
        {items?.map((item, idx) => (
          <LevelUpItem key={idx} {...item} position={idx + 1} />
        ))}
      </div>
    </div>
  );
};

export default LevelUpSection;
